import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, switchMap, tap } from 'rxjs';
import { InvitationPreview, LoginResult, TenantMembership, UpdateMeRequest } from './models';
import { AUTH_API_BASE, TOKEN_STORE } from './token-store';

/**
 * Client-side auth state + operations. Holds the session as signals (token, active
 * tenant/role, memberships) so components and route guards react without RxJS
 * plumbing. The token is persisted via the swappable {@link TOKEN_STORE}.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(TOKEN_STORE);
  private readonly base = `${inject(AUTH_API_BASE)}/auth`;

  private readonly _token = signal<string | null>(this.store.get());
  private readonly _activeTenantId = signal<string | null>(null);
  private readonly _activeRole = signal<string | null>(null);
  private readonly _memberships = signal<TenantMembership[]>([]);
  private readonly logoutHooks: (() => void)[] = [];

  constructor() {
    // Rehydrate the active tenant/role from the stored token's claims so role-gated
    // UI + route guards work on a cold load/refresh (not only right after login()).
    const stored = this._token();
    if (stored) {
      const claims = decodeJwtClaims(stored);
      this._activeTenantId.set(claims?.tenantId || null);
      this._activeRole.set(claims?.role || null);
    }
  }

  /** The current access token (null when logged out). */
  readonly accessToken = this._token.asReadonly();
  /** Whether a session exists. */
  readonly isAuthenticated = computed(() => this._token() !== null);
  /** The tenant the current token is scoped to (null when the user has no membership). */
  readonly activeTenantId = this._activeTenantId.asReadonly();
  /** The user's role in the active tenant. */
  readonly activeRole = this._activeRole.asReadonly();
  /** All tenants the user belongs to (the chooser). */
  readonly memberships = this._memberships.asReadonly();

  loginWithPassword(email: string, password: string): Observable<LoginResult> {
    return this.http.post<LoginResult>(`${this.base}/login`, { email, password }).pipe(tap((r) => this.apply(r)));
  }

  /** Re-mint the token for another tenant the user belongs to. */
  switchTenant(tenantId: string): Observable<LoginResult> {
    return this.http.post<LoginResult>(`${this.base}/switch-tenant`, { tenantId }).pipe(tap((r) => this.apply(r)));
  }

  /**
   * Adopt a token minted by another flow (V2.5: the forvalter portal's
   * board-scoped switch-in). Replaces the stored session; memberships are
   * left untouched (the caller re-reads them on demand).
   */
  adoptSession(accessToken: string, tenantId: string, role: string): void {
    this.store.set(accessToken);
    this._token.set(accessToken);
    this._activeTenantId.set(tenantId);
    this._activeRole.set(role);
  }

  /**
   * Elevate the session (WebAuthn step-up). Styre passkey → AAL2; superuser hardware
   * key → AAL3 (required by the platform-admin console). Re-mints + stores the token.
   */
  stepUp(tenantId: string, assertion: string): Observable<LoginResult> {
    return this.http.post<LoginResult>(`${this.base}/step-up`, { tenantId, assertion }).pipe(tap((r) => this.apply(r)));
  }

  /** Self-service account edit (PUT /api/auth/me). 422 on a wrong current password, 409 on a taken email. */
  updateMe(body: UpdateMeRequest): Observable<void> {
    return this.http.put<void>(`${this.base}/me`, body);
  }

  /** Sets a new password; `currentPassword` is null only for Vipps-only accounts that have none yet. */
  changePassword(currentPassword: string | null, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.base}/change-password`, { currentPassword, newPassword });
  }

  /**
   * «Glemt passord»: always succeeds (202) — the API never reveals whether the address has an account.
   * `app` (kundeapp | styreapp | adminkonsoll) decides which app's «nytt passord» page the mailed link opens.
   */
  forgotPassword(email: string, app?: string): Observable<void> {
    return this.http.post<void>(`${this.base}/forgot-password`, { email, app });
  }

  /** Sets a new password from the mailed one-hour link. 422 when the link is used or expired. */
  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.base}/reset-password`, { token, newPassword });
  }

  /** The invitation behind a mailed link. 404 when the token matches nothing. */
  invitation(token: string): Observable<InvitationPreview> {
    return this.http.get<InvitationPreview>('/api/onboarding/invitation', { params: { token } });
  }

  /** New resident: creates the account, binds the membership and starts the session in one call. */
  acceptInvitationAsNew(body: { token: string; name: string; password: string; email?: string }): Observable<LoginResult> {
    return this.http.post<LoginResult>('/api/onboarding/accept-new', body).pipe(tap((r) => this.apply(r)));
  }

  /** Existing (logged-in) user: binds the membership, then re-mints the token for that sameie. */
  acceptInvitation(token: string, tenantId: string): Observable<LoginResult> {
    return this.http.post('/api/onboarding/accept', { token }).pipe(switchMap(() => this.switchTenant(tenantId)));
  }

  /** Registers work that must run while the session is still valid (e.g. unregistering a push token). */
  beforeLogout(fn: () => void): void {
    this.logoutHooks.push(fn);
  }

  logout(): void {
    for (const fn of this.logoutHooks) fn();
    this.store.clear();
    this._token.set(null);
    this._activeTenantId.set(null);
    this._activeRole.set(null);
    this._memberships.set([]);
  }

  private apply(result: LoginResult): void {
    this.store.set(result.accessToken);
    this._token.set(result.accessToken);
    this._activeTenantId.set(result.activeTenantId);
    this._activeRole.set(result.role);
    this._memberships.set(result.memberships);
  }
}

/** Decode the (unverified) payload of a JWT to read the tenant/role claims client-side. */
function decodeJwtClaims(token: string): { tenantId?: string; role?: string } | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as { tenantId?: string; role?: string };
  } catch {
    return null;
  }
}
